import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Scissors, Layers, Play, Pause, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  STORY_SEGMENT_SECONDS,
  formatClock,
  segmentCountFor,
} from '@/utils/storySegments';
import { PreviewMuteToggle } from '@/components/Upload/PreviewMuteToggle';
import { tr } from '@/contexts/LanguageContext';

// ============================================================================
// StoryTrimDialog — frontend-only trim experience
// ============================================================================
//
// Visual prototype of a manual trim screen, opened automatically when a
// picked video is longer than STORY_SEGMENT_SECONDS (60 s).
//
// What this component does (entirely client-side):
//   · Loads the picked file in a vertical 9:16 preview.
//   · Captures 8 thumbnails along the video so the timeline strip
//     looks like a real editor (no real frames if Safari refuses to
//     seek — falls back to a colored gradient strip).
//   · Renders two draggable handles (start / end) over the strip.
//   · Honours touch + mouse + keyboard (←/→ to nudge the focused handle
//     by 1 s, Shift to nudge by 5 s).
//   · Shows mm:ss for start, end, and selected duration.
//   · Auto-clamps the selection so it never exceeds 60 s.
//
// What this component does NOT do:
//   · Process the file. It just records the trim window in state and
//     calls `onTrim({ start, end })`.
//   · Make any API calls.
//
// PROPS
//   file, videoUrl       picked File + object URL
//   durationSeconds      total length (number)
//   onCancel             go back to the source step
//   onTrim({start, end}) user accepted the trim
//   onSkipAndSplit       user picked the "split into N parts" path

const THUMB_COUNT = 8;
const THUMB_W = 80;
const THUMB_H = 120;

// Capture frame thumbnails for the timeline. Same defensive contract as
// SegmentsInfoCard: render-tolerant, never throws.
const captureStripThumbnails = (url, total, onTile, abortRef) => {
  const v = document.createElement('video');
  v.preload = 'auto';
  v.muted = true;
  v.playsInline = true;
  v.src = url;

  const canvas = document.createElement('canvas');
  canvas.width = THUMB_W;
  canvas.height = THUMB_H;
  const ctx = canvas.getContext('2d');
  let i = 0;
  const stops = Array.from({ length: THUMB_COUNT }, (_, k) =>
    Math.min(total - 0.05, (total / THUMB_COUNT) * (k + 0.5))
  );

  const next = () => {
    if (abortRef.current) return;
    if (i >= stops.length) return;
    const handler = () => {
      try {
        ctx.drawImage(v, 0, 0, THUMB_W, THUMB_H);
        onTile(i, canvas.toDataURL('image/jpeg', 0.6));
      } catch {
        onTile(i, null);
      }
      v.removeEventListener('seeked', handler);
      i += 1;
      next();
    };
    v.addEventListener('seeked', handler, { once: true });
    try { v.currentTime = stops[i]; } catch { onTile(i, null); i += 1; next(); }
  };

  const onMeta = () => next();
  v.addEventListener('loadedmetadata', onMeta, { once: true });
  v.addEventListener('error', () => {}, { once: true });
};

export const StoryTrimDialog = ({
  videoUrl,
  durationSeconds,
  onCancel,
  onTrim,
  onSkipAndSplit,
}) => {
  const total = Math.max(1, Number(durationSeconds) || 0);
  const trackRef = useRef(null);
  const videoRef = useRef(null);
  const abortRef = useRef(false);

  // Selection window in seconds. Start clamped to 0; end clamped to
  // min(total, start + 60).
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(Math.min(total, STORY_SEGMENT_SECONDS));
  const [thumbs, setThumbs] = useState(() => Array(THUMB_COUNT).fill(null));
  const [playing, setPlaying] = useState(false);
  const [focusHandle, setFocusHandle] = useState('start');

  // Capture timeline thumbnails on mount.
  useEffect(() => {
    abortRef.current = false;
    if (videoUrl) {
      captureStripThumbnails(videoUrl, total, (i, src) => {
        if (abortRef.current) return;
        setThumbs((prev) => {
          const next = prev.slice();
          next[i] = src;
          return next;
        });
      }, abortRef);
    }
    return () => { abortRef.current = true; };
  }, [videoUrl, total]);

  // Keep the preview video looping inside the [start, end] window.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onTime = () => {
      if (v.currentTime >= end) {
        v.currentTime = start;
        if (!v.paused) v.play().catch(() => {});
      } else if (v.currentTime < start) {
        v.currentTime = start;
      }
    };
    v.addEventListener('timeupdate', onTime);
    return () => v.removeEventListener('timeupdate', onTime);
  }, [start, end]);

  // Drag handlers — using pointer events for touch + mouse uniformity.
  const dragRef = useRef(null);

  const pxToSec = (clientX) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    return (x / rect.width) * total;
  };

  // Pointer-drag handlers — track which handle is being dragged via a
  // ref so we don't re-render on every mousemove. The visible selection
  // lives in setState.
  const onPointerDown = (which) => (e) => {
    if (e.cancelable) e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = which;
    setFocusHandle(which);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const sec = pxToSec(e.clientX);
    if (dragRef.current === 'start') {
      const nextStart = Math.max(0, Math.min(sec, end - 1));
      const clampedEnd = Math.min(end, nextStart + STORY_SEGMENT_SECONDS);
      setStart(nextStart);
      if (clampedEnd !== end) setEnd(clampedEnd);
    } else {
      const nextEnd = Math.min(total, Math.max(sec, start + 1));
      const clampedStart = Math.max(start, nextEnd - STORY_SEGMENT_SECONDS);
      setEnd(nextEnd);
      if (clampedStart !== start) setStart(clampedStart);
    }
  };
  const onPointerUp = () => { dragRef.current = null; };

  // Keyboard nudge — accessibility.
  const onKeyDown = (e) => {
    const step = e.shiftKey ? 5 : 1;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const dir = e.key === 'ArrowLeft' ? -1 : 1;
    if (focusHandle === 'start') {
      const next = Math.max(0, Math.min(start + dir * step, end - 1));
      const clampedEnd = Math.min(end, next + STORY_SEGMENT_SECONDS);
      setStart(next);
      if (clampedEnd !== end) setEnd(clampedEnd);
    } else {
      const next = Math.min(total, Math.max(end + dir * step, start + 1));
      const clampedStart = Math.max(start, next - STORY_SEGMENT_SECONDS);
      setEnd(next);
      if (clampedStart !== start) setStart(clampedStart);
    }
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.currentTime = Math.max(start, Math.min(v.currentTime, end - 0.1));
      v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const selectedDuration = Math.max(0, end - start);
  const exceedsSegment = selectedDuration > STORY_SEGMENT_SECONDS + 0.01;
  const totalSplits = segmentCountFor(total);

  // Map start/end to % across the track for layout.
  const startPct = (start / total) * 100;
  const endPct = (end / total) * 100;

  return (
    <div
      className="absolute inset-0 z-30 flex flex-col bg-card"
      data-testid="story-trim-dialog"
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={onCancel}
          data-testid="story-trim-back-btn"
          className="w-8 h-8 rounded-full hover:bg-secondary inline-flex items-center justify-center"
          aria-label={tr("Volver")}
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-display font-bold leading-tight inline-flex items-center gap-1.5">
            <Scissors className="w-4 h-4 text-primary" />{tr("Recortar tu historia")}</p>
          <p className="text-[11px] text-muted-foreground leading-tight">{tr("Máximo {0} s por historia.", STORY_SEGMENT_SECONDS)}</p>
        </div>
      </div>

      {/* Scrollable body — keeps the "Usar este recorte" CTA always visible. */}
      <div className="flex-1 min-h-0 overflow-y-auto">

      {/* Vertical 9:16 preview */}
      <div className="bg-black aspect-[9/16] relative flex items-center justify-center">
        <video
          ref={videoRef}
          src={videoUrl}
          autoPlay={false}
          loop={false}
          muted
          playsInline
          className="w-full h-full object-contain"
          data-testid="story-trim-preview-video"
        />
        {/* Iter 110 — mute toggle (pinned bottom-left so it doesn't
            collide with any future right-side CTAs). */}
        <PreviewMuteToggle
          videoRef={videoRef}
          testIdSuffix="story-trim"
          className="absolute bottom-3 left-3 z-30"
        />
        <button
          type="button"
          onClick={togglePlay}
          data-testid="story-trim-play-btn"
          className="absolute inset-0 flex items-center justify-center"
          aria-label={playing ? tr("Pausar") : tr("Reproducir")}
        >
          {!playing && (
            <span className="w-14 h-14 rounded-full bg-black/55 backdrop-blur-md border border-white/15 flex items-center justify-center">
              <Play className="w-6 h-6 text-white" />
            </span>
          )}
          {playing && (
            <span className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/55 backdrop-blur-md border border-white/15 flex items-center justify-center">
              <Pause className="w-4 h-4 text-white" />
            </span>
          )}
        </button>

        {/* Selected duration pill */}
        <div
          className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full bg-black/65 backdrop-blur-md text-white text-[11px] font-bold inline-flex items-center gap-1"
          data-testid="story-trim-duration-pill"
        >
          <Scissors className="w-3 h-3" />
          {formatClock(start)} – {formatClock(end)} · {formatClock(selectedDuration)}
        </div>
      </div>

      {/* Timeline */}
      <div className="px-4 pt-4">
        <div
          ref={trackRef}
          tabIndex={0}
          onKeyDown={onKeyDown}
          data-testid="story-trim-timeline"
          className="relative w-full h-16 rounded-lg overflow-hidden border border-border bg-secondary focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {/* Thumbnail strip background */}
          <div className="absolute inset-0 flex">
            {thumbs.map((src, i) => (
              <div
                key={i}
                className="flex-1 border-r border-black/20 last:border-r-0 bg-black/40"
                style={src ? { backgroundImage: `url(${src})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
              />
            ))}
          </div>
          {/* Dimmed regions outside the selection */}
          <div
            className="absolute inset-y-0 left-0 bg-background/70 backdrop-blur-[1px] pointer-events-none"
            style={{ width: `${startPct}%` }}
          />
          <div
            className="absolute inset-y-0 right-0 bg-background/70 backdrop-blur-[1px] pointer-events-none"
            style={{ width: `${100 - endPct}%` }}
          />
          {/* Selection border */}
          <div
            className="absolute inset-y-0 border-y-2 border-primary pointer-events-none"
            style={{ left: `${startPct}%`, right: `${100 - endPct}%` }}
            data-testid="story-trim-selection"
          />
          {/* Handles */}
          <button
            type="button"
            aria-label={tr("Inicio")}
            data-testid="story-trim-handle-start"
            onPointerDown={onPointerDown('start')}
            onFocus={() => setFocusHandle('start')}
            className="absolute top-0 bottom-0 -translate-x-1/2 w-3 cursor-ew-resize touch-none"
            style={{ left: `${startPct}%` }}
          >
            <span className="absolute inset-y-1 left-1/2 -translate-x-1/2 w-1.5 rounded bg-primary shadow-md" />
            <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-mono text-primary whitespace-nowrap">
              {formatClock(start)}
            </span>
          </button>
          <button
            type="button"
            aria-label={tr("Fin")}
            data-testid="story-trim-handle-end"
            onPointerDown={onPointerDown('end')}
            onFocus={() => setFocusHandle('end')}
            className="absolute top-0 bottom-0 -translate-x-1/2 w-3 cursor-ew-resize touch-none"
            style={{ left: `${endPct}%` }}
          >
            <span className="absolute inset-y-1 left-1/2 -translate-x-1/2 w-1.5 rounded bg-primary shadow-md" />
            <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] font-mono text-primary whitespace-nowrap">
              {formatClock(end)}
            </span>
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground text-center mt-7">{tr("Desliza los marcadores para elegir hasta {0} s · ←/→ para ajustar", STORY_SEGMENT_SECONDS)}</p>
      </div>

      </div>{/* /scroll body */}

      {/* Actions — pinned footer with safe-area */}
      <div className="flex-shrink-0 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] space-y-2 border-t border-border bg-card">
        <Button
          onClick={() => onTrim?.({ start, end })}
          disabled={selectedDuration < 1 || exceedsSegment}
          data-testid="story-trim-confirm-btn"
          className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5 font-semibold"
        >
          <Scissors className="w-4 h-4" />{tr("Usar este recorte ({0})", formatClock(selectedDuration))}</Button>
        {totalSplits > 1 && (
          <motion.button
            type="button"
            onClick={onSkipAndSplit}
            data-testid="story-trim-skip-split-btn"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground py-2"
          >
            <Layers className="w-3.5 h-3.5" />{tr("Saltar y dividir en {0} partes", totalSplits)}</motion.button>
        )}
      </div>
    </div>
  );
};

export default StoryTrimDialog;
